import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {
  desktopAndUpLinaria,
  TabContentBox,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, useCallback, useEffect, useMemo, useRef} from 'react';
import {ProductTileConfig} from '../../../features/Recommendations/ProductList';
import {useProductDetailsReviewsABTest} from '../ReviewsSection/utils/useReviewsABTest';
import {ScrollShopTheLookIntoView} from '../ShopTheLookScrollIntoView/ScrollShowTheLookIntoView';
import {useShopTheLookScrollManager} from '../ShopTheLookScrollIntoView/useShopTheLookScrollManager';
import {
  REVIEWS_SECTION_NAME,
  ReviewsSectionTab,
  ScrollReviewsSectionIntoView,
} from './tabs/ReviewsSectionTab';
import {SHOP_THE_LOOK_TAB_NAME, ShopTheLookTab} from './tabs/ShopTheLookTab';
import {useProductReviewSectionScrollManager} from './tabs/useProductReviewSectionScrollManager';
import {
  useRatingsAndReviewsSection,
  useShopTheLookSection,
} from './useGetProductDetailsBulkSection';

export const ProductInformationBox: FC<{
  onAddToBasketCtaClick?: ({
    productId,
    couponPromotionId,
  }: {
    productId: number;
    couponPromotionId?: string;
  }) => void;
  onAddToBasketSuccess: () => void;
  productTileConfig?: ProductTileConfig;
}> = ({onAddToBasketCtaClick, onAddToBasketSuccess, productTileConfig}) => {
  const [inView, setInView] = useHistoryPageState(false, {
    uniqueKey: 'ProductInformationBox::inView',
  });
  const {isActive: isDesktopOuftiSlidersEnabled} = useIsFeatureActive(
    'enable_desktop_outfit_slider',
  );
  // the ref is used to "lock-in" the inView state
  // this avoid flickering of the tab content when dropdowns are triggered
  const hasBeenInView = useRef(inView);
  const handleInView = useCallback(
    (updatedInView: boolean) => {
      if (updatedInView) {
        hasBeenInView.current = true;
      }
      setInView(updatedInView);
    },
    [setInView],
  );

  const {ref} = useInViewEffect<HTMLDivElement>(handleInView, {
    threshold: 0,
    triggerOnce: true,
    rootMargin: '250px',
    initialInView: inView,
  });

  const activeTab = useInitialActiveTab({
    inView,
  });

  if (isDesktopOuftiSlidersEnabled) {
    return null;
  }

  return (
    <Wrapper ref={ref} data-testid="productInformationBox">
      <ScrollShopTheLookIntoView />
      <ScrollReviewsSectionIntoView />
      {activeTab && (
        <TabContentBox defaultHeight={750} defaultItem={SHOP_THE_LOOK_TAB_NAME}>
          <>
            {hasBeenInView.current && (
              <>
                <ShopTheLookTab
                  onAddToBasketCtaClick={onAddToBasketCtaClick}
                  onAddToBasketSuccess={onAddToBasketSuccess}
                  productTileConfig={productTileConfig}
                />
                <ReviewsSectionTab />
                <UpdateDefaultTab activeTab={activeTab} />
                <UpdateTabBasedOnScrollManager inView={inView} />
              </>
            )}
          </>
        </TabContentBox>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  max-width: 1500px;
  width: 100%;
  margin: 0 auto 8rem;
  padding: 0 1.5rem;
  box-sizing: border-box;
  ${desktopAndUpLinaria(`
    width: 83.333%;
  `)}
`;

const useInitialActiveTab = ({inView}: {inView: boolean}) => {
  const hasShopTheLookData =
    useShopTheLookSection(inView).data?.shopTheLook?.shopTheLook;

  const hasRatingsAndReviewsData = Boolean(
    useRatingsAndReviewsSection(inView).data?.summary,
  );

  const {showReviewsOnProductDetails} = useProductDetailsReviewsABTest(
    hasRatingsAndReviewsData,
  );

  const activeTab = useMemo(() => {
    if (hasShopTheLookData) {
      return SHOP_THE_LOOK_TAB_NAME;
    }
    if (hasRatingsAndReviewsData && showReviewsOnProductDetails) {
      return REVIEWS_SECTION_NAME;
    }

    return '';
  }, [
    hasRatingsAndReviewsData,
    showReviewsOnProductDetails,
    hasShopTheLookData,
  ]);

  return activeTab;
};

const UpdateDefaultTab = ({activeTab}: {activeTab: string}) => {
  const setActiveTab = TabContentBox.useUpdateTab();

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
    setActiveTab(activeTab);
  }, [activeTab, setActiveTab]);

  return null;
};

const UpdateTabBasedOnScrollManager = ({inView}: {inView: boolean}) => {
  const setActiveTab = TabContentBox.useUpdateTab();

  const hasRatingsAndReviewsData = Boolean(
    useRatingsAndReviewsSection(inView).data?.summary,
  );

  const {showReviewsOnProductDetails} = useProductDetailsReviewsABTest(
    hasRatingsAndReviewsData,
  );

  const hasShopTheLookData =
    useShopTheLookSection(inView).data?.shopTheLook?.shopTheLook;

  const {shouldScrollToSection: scrollToReviewsSectionInitiated} =
    useProductReviewSectionScrollManager();

  const {shouldScrollShopTheLookIntoView: scrollToShopTheLookInitiated} =
    useShopTheLookScrollManager();

  useEffect(() => {
    if (showReviewsOnProductDetails && scrollToReviewsSectionInitiated) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setActiveTab(REVIEWS_SECTION_NAME);
    }

    if (hasShopTheLookData && scrollToShopTheLookInitiated) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setActiveTab(SHOP_THE_LOOK_TAB_NAME);
    }
  }, [
    hasShopTheLookData,
    scrollToReviewsSectionInitiated,
    scrollToShopTheLookInitiated,
    setActiveTab,
    showReviewsOnProductDetails,
  ]);

  return null;
};
